import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Plus, Calendar, RefreshCw, Target, CreditCard, Edit, Trash2, MoreHorizontal, CheckCircle, History, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import axios from 'axios';
import FundingTermsModal from '../components/FundingTermsModal';
import UploadDocumentsDealModal from '../components/UploadDocumentsDealModal';
import NewPaymentModal from '../components/NewPaymentModal';
import EditDealModal from '../components/EditDealModal';
import RefundModal from '../components/RefundModal';
import AddSyndicatorModal from '../components/AddSyndicatorModal';
import RefinanceDealModal from '../components/RefinanceDealModal';
import DealStatusDropdown, { normalizeStatus, getStatusMeta } from '../components/DealStatusDropdown';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const DealDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showUploadDocs, setShowUploadDocs] = useState(false);
  const [showNewPayment, setShowNewPayment] = useState(false);
  const [showEditDeal, setShowEditDeal] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [showRefinance, setShowRefinance] = useState(false);
  const [syndicators, setSyndicators] = useState([]);
  const [showAddSyndicator, setShowAddSyndicator] = useState(false);
  const [editingSyndicator, setEditingSyndicator] = useState(null);
  const [edSynForm, setEdSynForm] = useState({ percentage: '', commission_percent: '' });
  const [autoClearEnabled, setAutoClearEnabled] = useState(false);
  const [paymentDay, setPaymentDay] = useState('Monday');
  const [documents, setDocuments] = useState([]);
  const [editingPaymentNote, setEditingPaymentNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [showAddCommission, setShowAddCommission] = useState(false);
  const [newCommission, setNewCommission] = useState({ recipient_name: '', recipient_type: 'ISO', amount: '', notes: '' });
  const [statusHistory, setStatusHistory] = useState([]);
  const [showStatusHistory, setShowStatusHistory] = useState(false);

  useEffect(() => {
    loadDeal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDeal = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeal(response.data);
      setAutoClearEnabled(response.data.auto_clear_payments || false);
      setPaymentDay(response.data.payment_day || 'Monday');
      
      // Load syndicators if deal is funded
      if (response.data.status === 'funded' || response.data.type === 'funded') {
        loadSyndicators();
      }
    } catch (error) {
      console.error('Error loading deal:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals/${id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    }
  };

  const loadClientInfo = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/clients/by-deal/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.data.error) {
        setClientInfo(response.data);
      }
    } catch (error) {
      console.error('Error loading client info:', error);
    }
  };

  const loadCommissions = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals/${id}/commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissions(response.data || []);
    } catch (error) {
      console.error('Error loading commissions:', error);
    }
  };

  const addCommission = async () => {
    if (!newCommission.recipient_name || !newCommission.amount) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${id}/commissions`, {
        recipient_name: newCommission.recipient_name,
        recipient_type: newCommission.recipient_type,
        amount: parseFloat(newCommission.amount),
        notes: newCommission.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewCommission({ recipient_name: '', recipient_type: 'ISO', amount: '', notes: '' });
      setShowAddCommission(false);
      loadCommissions();
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add commission', variant: 'destructive' });
    }
  };

  const deleteCommission = async (commId) => {
    if (!window.confirm('Delete this commission entry?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/deals/${id}/commissions/${commId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadCommissions();
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete commission', variant: 'destructive' });
    }
  };

  const downloadDocument = async (docId, fileName) => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals/${id}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download document', variant: 'destructive' });
    }
  };

  useEffect(() => {
    loadDeal();
    loadDocuments();
    loadClientInfo();
    loadCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSyndicators = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const response = await axios.get(`${API_BASE}/deals/${id}/syndicators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyndicators(response.data || []);
    } catch (error) {
      console.error('Error loading syndicators:', error);
    }
  };

  const deleteSyndicator = async (syndicatorId) => {
    if (!window.confirm('Remove this syndicator from the deal?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/deals/${id}/syndicators/${syndicatorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Syndicator removed' });
      loadSyndicators();
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove syndicator', variant: 'destructive' });
    }
  };

  const openEditSyndicator = (syn) => {
    setEditingSyndicator(syn);
    setEdSynForm({
      percentage: String(syn.percentage ?? ''),
      commission_percent: String(syn.commission_percent ?? ''),
    });
  };

  const saveSyndicatorEdit = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const payload = {};
      if (edSynForm.percentage !== '') payload.percentage = parseFloat(edSynForm.percentage);
      if (edSynForm.commission_percent !== '') payload.commission_percent = parseFloat(edSynForm.commission_percent);
      await axios.put(
        `${API_BASE}/deals/${id}/syndicators/${editingSyndicator.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Saved', description: 'Syndicator updated' });
      setEditingSyndicator(null);
      loadSyndicators();
      loadDeal();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || error.response?.data?.error || 'Failed to update syndicator',
        variant: 'destructive'
      });
    }
  };

  const approveDeal = async () => {
    if (!window.confirm('Move this deal to Underwriting?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Deal moved to Underwriting' });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve deal', variant: 'destructive' });
    }
  };

  const deleteDeal = async () => {
    if (!window.confirm('Are you sure you want to delete this deal? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(`${API_BASE}/deals/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Deal deleted successfully' });
      navigate('/submitted-deals');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete deal', variant: 'destructive' });
    }
  };

  const setToDefault = async () => {
    if (!window.confirm('Mark this deal as Default?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${id}/status`, { status: 'Default' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Deal marked as Default' });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const toggleCRMFees = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const newState = !deal.crm_fees_active;
      await axios.put(`${API_BASE}/deals/${id}`, { crm_fees_active: newState }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: `CRM Fees ${newState ? 'activated' : 'deactivated'}` });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to toggle CRM fees', variant: 'destructive' });
    }
  };

  const toggleAutoClear = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const newState = !autoClearEnabled;
      await axios.put(`${API_BASE}/deals/${id}`, { 
        auto_clear_payments: newState,
        payment_day: paymentDay 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoClearEnabled(newState);
      toast({ title: 'Success', description: `Auto-clear ${newState ? 'enabled' : 'disabled'}` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to toggle auto-clear', variant: 'destructive' });
    }
  };

  const updatePaymentDay = async (day) => {
    setPaymentDay(day);
    if (autoClearEnabled) {
      try {
        const token = localStorage.getItem('mcaToken');
        await axios.put(`${API_BASE}/deals/${id}`, { payment_day: day }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({ title: 'Success', description: `Payment day set to ${day}` });
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to update payment day', variant: 'destructive' });
      }
    }
  };

  const markPaymentMissed = async (paymentNumber) => {
    if (!window.confirm('Mark this payment as missed?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${id}/payments/${paymentNumber}/miss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Payment marked as missed' });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to mark payment', variant: 'destructive' });
    }
  };

  const markPaymentReturned = async (paymentNumber) => {
    if (!window.confirm('Mark this payment as returned (NSF/R01)?')) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${id}/payments/${paymentNumber}/return`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Payment Returned', description: 'Payment marked as returned — notification sent to deal owner' });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to mark payment as returned', variant: 'destructive' });
    }
  };

  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({ amount: '', due_date: '', status: '', note: '' });
  const [deletingPayment, setDeletingPayment] = useState(null);

  const clearPayment = async (dealId, paymentNumber) => {
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${dealId}/payments/${paymentNumber}/clear`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Payment cleared' });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to clear payment', variant: 'destructive' });
    }
  };

  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    const dd = payment.due_date
      ? String(payment.due_date).slice(0, 10)
      : '';
    setEditPaymentForm({
      amount: String(payment.amount ?? deal.payment_amount ?? ''),
      due_date: dd,
      status: payment.status || 'pending',
      note: payment.note || '',
    });
  };

  const saveEditPayment = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const body = {};
      if (editPaymentForm.amount !== '') body.amount = parseFloat(editPaymentForm.amount);
      if (editPaymentForm.due_date) body.due_date = editPaymentForm.due_date;
      if (editPaymentForm.status) body.status = editPaymentForm.status;
      if (editPaymentForm.note !== undefined) body.note = editPaymentForm.note;
      await axios.put(
        `${API_BASE}/deals/${id}/payments/${editingPayment.payment_number}/edit`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Updated', description: `Payment #${editingPayment.payment_number} saved (P&L recalculated).` });
      setEditingPayment(null);
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || error.response?.data?.error || 'Failed to update', variant: 'destructive' });
    }
  };

  const confirmDeletePayment = async () => {
    if (!deletingPayment) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.delete(
        `${API_BASE}/deals/${id}/payments/${deletingPayment.payment_number}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Removed', description: `Payment #${deletingPayment.payment_number} deleted (P&L recalculated).` });
      setDeletingPayment(null);
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || error.response?.data?.error || 'Failed to delete', variant: 'destructive' });
    }
  };

  const addPaymentNote = async (paymentNumber) => {
    const note = prompt(`Add note for payment #${paymentNumber}:`);
    if (!note) return;
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${id}/payments/${paymentNumber}/note`, 
        { note },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast({ title: 'Success', description: 'Payment note added' });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add note', variant: 'destructive' });
    }
  };

  const handleArchiveDeal = async () => {
    if (!window.confirm(`Archive ${deal.business}? It will move to Archived Deals.`)) return;
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Deal archived' });
      navigate('/funded-deals');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to archive deal', variant: 'destructive' });
    }
  };

  const loadStatusHistory = async () => {
    try {
      const token = localStorage.getItem('mcaToken');
      const res = await axios.get(`${API_BASE}/deals/${id}/status-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setStatusHistory([]);
    }
  };

  const onStatusChanged = (newStatus) => {
    // Refresh deal + history after status change so P&L / auto-clear flags reflect latest
    loadDeal();
    loadStatusHistory();
  };

  useEffect(() => { if (id) loadStatusHistory(); }, [id]);

  const changeStatus = async (newStatus) => {
    if (isDefault && newStatus !== 'Default') {
      const confirmed = window.confirm(
        'This deal is in Default status. Changing status will affect:\n' +
        '- Payment tracking\n' +
        '- Auto-clearing (will be disabled)\n' +
        '- Legal/collections status\n\n' +
        'Have you resolved all outstanding issues?'
      );
      if (!confirmed) return;
    }
    
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.put(`${API_BASE}/deals/${id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Disable auto-clear if moving to Default
      if (newStatus === 'Default' && autoClearEnabled) {
        await axios.put(`${API_BASE}/deals/${id}`, { auto_clear_payments: false }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAutoClearEnabled(false);
      }
      
      toast({ title: 'Success', description: `Status changed to ${newStatus}` });
      loadDeal();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const isSubmitted = deal?.status === 'New' || deal?.status === 'Declined' || (!deal?.status && deal?.type !== 'funded');
  const isUnderwriting = deal?.status === 'underwriting' || deal?.status === 'In Underwriting';
  const isFunded = deal?.status === 'funded' || deal?.type === 'funded';
  const isDefault = deal?.paid_status === 'Default';
  const isLocked = isFunded && (normalizeStatus(deal?.paid_status) === 'Fully Paid' || deal?.payoff_locked === true);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold">Deal not found</h2>
        <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
      </div>
    );
  }

  // Paid-in % = collected / net wired (what % of our money has come back)
  const netBase = deal.net_amount != null ? deal.net_amount : (deal.net_funded != null ? deal.net_funded : (deal.advanced || 0));
  const collectedPercent = netBase > 0 ? ((deal.collected / netBase) * 100).toFixed(2) : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg transition"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800" data-testid="deal-detail-display-number">Deal #{deal.display_number ?? deal.id}</h1>
            <p className="text-sm text-gray-500 mt-1">Status: {deal.status || 'Submitted'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {isFunded && (
            <div className="flex items-center gap-2" data-testid="deal-status-area">
              <span className="text-sm text-gray-500">Deal Status:</span>
              <DealStatusDropdown
                dealId={id}
                currentStatus={deal?.paid_status}
                onChanged={onStatusChanged}
                size="lg"
              />
              <button
                type="button"
                onClick={() => setShowStatusHistory(true)}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 underline"
                data-testid="status-history-btn"
                title="View status change history"
              >
                <History size={14} /> History
              </button>
            </div>
          )}
          {isSubmitted && (
            <>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={approveDeal} data-testid="approve-deal-btn">
                <CheckCircle size={16} className="mr-2" />
                Approve & Move to Underwriting
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowFundingModal(true)} data-testid="fund-deal-directly-btn">
                <CheckCircle size={16} className="mr-2" />
                Fund Deal Directly
              </Button>
            </>
          )}
          {isUnderwriting && (
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowFundingModal(true)} data-testid="move-to-funded-btn">
              <CheckCircle size={16} className="mr-2" />
              Move to Funded
            </Button>
          )}
        </div>
      </div>

      {/* Lock banner: deal is Fully Paid → all payment writes blocked until reverted */}
      {isLocked && (
        <div
          className="mb-6 flex items-center justify-between gap-3 rounded-lg border border-gray-300 bg-gray-100 px-4 py-3 text-gray-800"
          data-testid="payoff-lock-banner"
        >
          <div className="flex items-center gap-2">
            <Lock size={18} className="text-gray-700" />
            <div>
              <div className="text-sm font-semibold">Deal locked — Fully Paid</div>
              <div className="text-xs text-gray-600">
                All payment edits are disabled. Change status away from Fully Paid to make further edits.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-suggest banner: total collected matches/exceeds payback but not yet Fully Paid */}
      {isFunded && (deal?.collected || 0) >= (deal?.payback || 0) && (deal?.payback || 0) > 0 && normalizeStatus(deal?.paid_status) !== 'Fully Paid' && (
        <div
          className="mb-6 flex items-center justify-between gap-3 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-green-800"
          data-testid="fully-paid-suggest-banner"
        >
          <div className="flex items-center gap-2">
            <CheckCircle size={18} className="text-green-600" />
            <div>
              <div className="text-sm font-semibold">Total collected matches payback</div>
              <div className="text-xs text-green-700">
                Collected ${(deal?.collected || 0).toLocaleString()} of ${(deal?.payback || 0).toLocaleString()} — mark this deal as Fully Paid?
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const token = localStorage.getItem('mcaToken');
                await axios.put(`${API_BASE}/deals/${id}/status`, { status: 'Fully Paid' }, { headers: { Authorization: `Bearer ${token}` } });
                toast({ title: 'Status updated', description: 'Deal marked as Fully Paid' });
                loadDeal();
                loadStatusHistory();
              } catch (e) {
                toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
              }
            }}
            className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
            data-testid="fully-paid-suggest-apply"
          >
            Mark Fully Paid
          </button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mb-8 flex-wrap">
        <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowUploadDocs(true)}>
          <Upload size={16} className="mr-2" />
          Upload Documents
        </Button>
        <Button variant="outline" onClick={() => setShowNewPayment(true)} disabled={isLocked} data-testid="new-payment-btn">
          <Plus size={16} className="mr-2" />
          New Payment
        </Button>
        <Button variant="outline" onClick={() => toast({ title: 'Info', description: 'Schedule Payments: Use Funding Terms modal when moving to Funded status' })}>
          <Calendar size={16} className="mr-2" />
          Schedule Payments
        </Button>
        <Button variant="outline" onClick={() => setShowRefund(true)} disabled={isLocked} data-testid="refund-btn">
          <RefreshCw size={16} className="mr-2" />
          Refund
        </Button>
        {isFunded && !isLocked && (
          <Button
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={() => setShowRefinance(true)}
            data-testid="refinance-deal-btn"
          >
            <RefreshCw size={16} className="mr-2" />
            Refinance Deal
          </Button>
        )}
        <Button variant="outline" onClick={toggleCRMFees}>
          {deal?.crm_fees_active ? 'Deactivate' : 'Activate'} CRM Fees
        </Button>
        <Button variant="outline" onClick={() => toast({ title: 'Info', description: 'Assign Lenders: Add syndicators in the Syndicators section below' })}>
          <Target size={16} className="mr-2" />
          Assign Lenders
        </Button>
        <Button variant="outline" onClick={() => setShowEditDeal(true)}>
          <Edit size={16} className="mr-2" />
          Edit
        </Button>
        <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={deleteDeal}>
          <Trash2 size={16} className="mr-2" />
          Delete
        </Button>
        <Button variant="outline" onClick={handleArchiveDeal}>
          Archive
        </Button>
        <Button variant="outline" onClick={() => {
          const token = localStorage.getItem('mcaToken');
          window.open(`${API_BASE}/reports/csv?token=${token}&deal_id=${id}`, '_blank');
        }} data-testid="deal-export-csv">
          Export CSV
        </Button>
        <Button variant="outline" onClick={() => {
          const token = localStorage.getItem('mcaToken');
          window.open(`${API_BASE}/reports/pdf?token=${token}&deal_id=${id}`, '_blank');
        }} data-testid="deal-export-pdf">
          Export PDF
        </Button>
      </div>

      {/* Application / Client Information — always visible */}
      <div className="bg-white rounded-lg shadow p-6 mb-8" data-testid="application-info-section">
        <h3 className="text-xl font-bold mb-6">Application Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4">
          <div>
            <p className="text-sm text-gray-500">First Name</p>
            <p className="text-gray-900">{clientInfo?.first_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Last Name</p>
            <p className="text-gray-900">{clientInfo?.last_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Business Name</p>
            <p className="text-gray-900">{clientInfo?.business_name || clientInfo?.name || deal.business || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="text-gray-900">{clientInfo?.email || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Phone</p>
            <p className="text-gray-900">{clientInfo?.phone || clientInfo?.business_phone || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Date of Birth</p>
            <p className="text-gray-900">{clientInfo?.dob || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">SSN</p>
            <p className="text-gray-900">{clientInfo?.ssn || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">EIN</p>
            <p className="text-gray-900">{clientInfo?.ein || clientInfo?.federal_tax_id || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Industry</p>
            <p className="text-gray-900">{clientInfo?.industry || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Monthly Revenue</p>
            <p className="text-gray-900">{clientInfo?.revenue || clientInfo?.monthly_revenue || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Address</p>
            <p className="text-gray-900">{clientInfo?.address || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">City / State / ZIP</p>
            <p className="text-gray-900">
              {clientInfo?.city || clientInfo?.state || clientInfo?.zip
                ? `${clientInfo?.city || ''} ${clientInfo?.state || ''} ${clientInfo?.zip || ''}`.trim()
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Business Name */}

      {/* Payment Schedule for Funded Deals */}
      {isFunded && (deal?.payment_schedule || []).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">Payment Schedule</h3>
            <div className="flex items-center gap-4">
              {deal?.payment_frequency === 'weekly' && autoClearEnabled && !isDefault && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Payment Day:</label>
                  <select
                    value={paymentDay}
                    onChange={(e) => updatePaymentDay(e.target.value)}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Auto-clear:</label>
                <button
                  onClick={toggleAutoClear}
                  disabled={isDefault}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    autoClearEnabled && !isDefault ? 'bg-green-600' : 'bg-gray-300'
                  } ${isDefault ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      autoClearEnabled && !isDefault ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                {isDefault && <span className="text-xs text-red-600">(Disabled - Default)</span>}
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            {autoClearEnabled && !isDefault ? (
              <p>✓ Payments will auto-clear {deal?.payment_frequency === 'daily' ? 'at midnight every night' : `every ${paymentDay}`}</p>
            ) : (
              <p>Manual clearing only</p>
            )}
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b">
                  <th className="text-left py-2 px-4">#</th>
                  <th className="text-left py-2 px-4">Due Date</th>
                  <th className="text-left py-2 px-4">Amount</th>
                  <th className="text-left py-2 px-4">Status</th>
                  <th className="text-left py-2 px-4">Cleared Date</th>
                  <th className="text-left py-2 px-4">Note</th>
                  <th className="text-left py-2 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(deal?.payment_schedule || []).map((payment, idx) => {
                  const dueDate = new Date(payment.due_date);
                  const isOverdue = payment.status === 'pending' && dueDate < new Date();
                  const isMissed = payment.status === 'missed';
                  
                  return (
                    <tr key={idx} className={`border-b ${
                      payment.status === 'cleared' ? 'bg-green-50' :
                      isMissed ? 'bg-red-50' :
                      isOverdue ? 'bg-orange-50' : ''
                    }`}>
                      <td className="py-2 px-4">{payment.payment_number}</td>
                      <td className="py-2 px-4">{dueDate.toLocaleDateString()}</td>
                      <td className="py-2 px-4">${payment.amount}</td>
                      <td className="py-2 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          payment.status === 'cleared' ? 'bg-green-100 text-green-700' :
                          isMissed ? 'bg-red-100 text-red-700' :
                          isOverdue ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {payment.status === 'cleared' ? 'Cleared' : 
                           isMissed ? 'Missed' :
                           isOverdue ? 'Overdue' : 'Pending'}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        {payment.cleared_date ? new Date(payment.cleared_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-600 max-w-xs truncate">
                        {payment.note || '-'}
                      </td>
                      <td className="py-2 px-4">
                        <div className="flex gap-2">
                          {payment.status === 'pending' && !isLocked && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-xs px-2 py-1 h-7"
                                onClick={() => clearPayment(id, payment.payment_number)}
                              >
                                Clear
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50 text-xs px-2 py-1 h-7 border-red-300"
                                onClick={() => markPaymentMissed(payment.payment_number)}
                              >
                                Miss
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-orange-600 hover:bg-orange-50 text-xs px-2 py-1 h-7 border-orange-300"
                                onClick={() => markPaymentReturned(payment.payment_number)}
                              >
                                Return
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:bg-blue-50 text-xs px-2 py-1 h-7"
                            onClick={() => addPaymentNote(payment.payment_number)}
                          >
                            Note
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-gray-600 hover:bg-gray-100 text-xs px-2 py-1 h-7"
                            onClick={() => openEditPayment(payment)}
                            data-testid={`edit-payment-${payment.payment_number}`}
                            title={isLocked ? 'Locked — Fully Paid' : 'Edit payment'}
                            disabled={isLocked}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50 text-xs px-2 py-1 h-7 border-red-300"
                            onClick={() => setDeletingPayment(payment)}
                            data-testid={`delete-payment-${payment.payment_number}`}
                            title={isLocked ? 'Locked — Fully Paid' : 'Delete row'}
                            disabled={isLocked}
                          >
                            Del
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mb-6">
        <p className="text-sm text-gray-500 mb-1">Business Name:</p>
        <h2 className="text-4xl font-bold text-gray-900">{deal.business}</h2>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Remaining Balance:</p>
          <p className={`text-2xl font-bold ${deal.remaining < 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {deal.remaining < 0 ? '-' : ''}${Math.abs(deal.remaining || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Unallocated:</p>
          <p className="text-2xl font-bold">$0.00</p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Default fee:</p>
          <p className="text-2xl font-bold">$0.00</p>
        </div>
        <div className="bg-gray-100 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Wire fee:</p>
          <p className="text-2xl font-bold">$0.00</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">Collected:</p>
          <p className="text-2xl font-bold">${deal.collected?.toLocaleString() || '0.00'} ({collectedPercent}%)</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">P&L:</p>
          {(() => {
            const collected = deal.collected || 0;
            const netAmt = deal.net_amount != null ? deal.net_amount : (deal.net_funded != null ? deal.net_funded : (deal.advanced || 0));
            const pnl = deal.deal_pnl != null ? deal.deal_pnl : (collected - netAmt);
            const pnlNet = deal.deal_pnl_net_of_costs != null
              ? deal.deal_pnl_net_of_costs
              : (pnl - (deal.commission_total || 0) - (deal.total_expenses || 0));
            return (
              <>
                <p className={`text-2xl font-bold ${pnl >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="deal-pnl-primary">
                  ${pnl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
                <p className={`text-xs mt-1 ${pnlNet >= 0 ? 'text-green-700' : 'text-red-500'}`} data-testid="deal-pnl-net-of-costs" title="Net of commissions, expenses & syndicator payouts">
                  Net of costs: ${pnlNet.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </>
            );
          })()}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Left: General Information */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-6">General Information</h3>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <p className="text-sm text-gray-500">Unique Name</p>
              <p className="text-gray-900">{deal.business}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Advance Amount</p>
              <p className="text-gray-900">${deal.advanced?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Company</p>
              <p className="text-gray-900">{deal.inhouse || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">UCC Uploaded</p>
              <p className="text-gray-900">No</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">OutHouse Lenders</p>
              <p className="text-gray-900">N/A</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Term</p>
              <p className="text-gray-900">{deal.term}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">ISO</p>
              <p className="text-gray-900">{deal.iso}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Start Date</p>
              <p className="text-gray-900">{deal.date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Created On</p>
              <p className="text-gray-900">{deal.date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Planned End Date</p>
              <p className="text-gray-900">n/a</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Updated On</p>
              <p className="text-gray-900">{deal.date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="text-gray-900">{deal.status || deal.paid_status || 'Current'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Client Id</p>
              <p className="text-gray-900">{deal.id}</p>
            </div>
          </div>
        </div>

        {/* Right: Contact Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-6">Contact Information</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-gray-400">Phone</span>
              <span className="text-gray-900">{clientInfo?.phone || clientInfo?.business_phone || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400">Mobile</span>
              <span className="text-gray-900">N/A</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400">Fax</span>
              <span className="text-gray-900">N/A</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400">Email</span>
              <span className="text-gray-900 text-sm">{clientInfo?.email || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400">Address</span>
              <span className="text-gray-900 text-sm">
                {clientInfo?.address 
                  ? `${clientInfo.address}${clientInfo.city ? ', ' + clientInfo.city : ''}${clientInfo.state ? ', ' + clientInfo.state : ''}${clientInfo.zip ? ' ' + clientInfo.zip : ''}`
                  : 'N/A'}
              </span>
            </div>
          </div>
          <div className="mt-6 bg-gray-800 h-48 rounded flex items-center justify-center text-white text-sm">
            Google Maps Placeholder
          </div>
        </div>
      </div>

      {/* Off-schedule Payments & Refunds — manual/extra payments that don't
          live on the payment schedule. Ensures no payment ever "disappears". */}
      {isFunded && ((deal?.manual_payments || []).length > 0 || (deal?.refunds || []).length > 0) && (
        <div className="bg-white rounded-lg shadow p-6 mb-8" data-testid="off-schedule-section">
          <h3 className="text-xl font-bold mb-4">Off-Schedule Payments & Refunds</h3>
          {(deal.manual_payments || []).length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Manual / Off-schedule Payments</p>
              <table className="w-full text-sm border" data-testid="off-schedule-payments-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Method</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.manual_payments.map((mp, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{mp.payment_date}</td>
                      <td className="px-3 py-2 text-right text-green-700 font-semibold">${(mp.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2">{mp.payment_method || ''}</td>
                      <td className="px-3 py-2 text-gray-600">{mp.notes || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(deal.refunds || []).length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Refunds</p>
              <table className="w-full text-sm border" data-testid="refunds-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {deal.refunds.map((r, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{r.refund_date}</td>
                      <td className="px-3 py-2 text-right text-red-600 font-semibold">-${(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2">{r.reason || ''}</td>
                      <td className="px-3 py-2 text-gray-600">{r.notes || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-3">
            These entries add to (or subtract from) the Collected total but don't live in the payment schedule above.
          </p>
        </div>
      )}

      {/* Payment Information */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-xl font-bold mb-6">Payment Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4">
          <div>
            <p className="text-sm text-gray-500">Frequency</p>
            <p className="text-gray-900">{deal.payment_frequency === 'daily' ? 'Daily' : deal.payment_frequency === 'weekly' ? 'Weekly' : (deal.daily ? 'Daily' : 'Weekly')}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Name on Voided Check</p>
            <p className="text-gray-900">{deal.voided_check_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Paper Grade</p>
            <p className="text-gray-900">{deal.paper_grade || deal.expected_grade || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Bank Name</p>
            <p className="text-gray-900">{deal.bank_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Payback Amount</p>
            <p className="text-gray-900">${deal.payback?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Early Pay-Off Amount</p>
            <p className="text-gray-900">$0.00</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Payments</p>
            <p className="text-gray-900">${deal.payment_amount?.toLocaleString() || deal.weekly || deal.daily}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Collected</p>
            <p className="text-gray-900">${deal.collected?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Remaining Balance</p>
            <p className="text-gray-900">${deal.remaining?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Default Fee</p>
            <p className="text-gray-900">$0.00</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Bank Fee</p>
            <p className="text-gray-900">$0.00</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Wire Fee</p>
            <p className="text-gray-900">$0.00</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">PSF Fee</p>
            <p className="text-gray-900">$0.00</p>
          </div>
        </div>
      </div>

      {/* Expenses */}
      {isFunded && (
        <div className="bg-white rounded-lg shadow p-6 mb-8" data-testid="expenses-section">
          <h3 className="text-xl font-bold mb-6">Expenses</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-4">
            <div>
              <p className="text-sm text-gray-500">UW Fee</p>
              <p className="text-gray-900 font-medium">${(deal.uw_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Legal Fee</p>
              <p className="text-gray-900 font-medium">${(deal.legal_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Filing Fee</p>
              <p className="text-gray-900 font-medium">${(deal.filing_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">UCC Fee</p>
              <p className="text-gray-900 font-medium">${(deal.ucc_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Bank/Wire Fee</p>
              <p className="text-gray-900 font-medium">${(deal.bank_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t flex justify-between items-center">
            <span className="text-sm font-semibold text-gray-700">Total Expenses</span>
            <span className="text-lg font-bold text-red-600">
              ${((deal.uw_fee || 0) + (deal.legal_fee || 0) + (deal.filing_fee || 0) + (deal.ucc_fee || 0) + (deal.bank_fee || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {/* Commissions */}
      {isFunded && (
        <div className="bg-white rounded-lg shadow p-6 mb-8" data-testid="commissions-section">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">Commissions</h3>
            {isAdmin && (
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => setShowAddCommission(!showAddCommission)} data-testid="add-commission-btn">
                + Add Commission
              </Button>
            )}
          </div>
          
          {showAddCommission && (
            <div className="bg-gray-50 p-4 rounded mb-4 grid grid-cols-5 gap-3 items-end">
              <div>
                <label className="block text-xs font-medium mb-1">Recipient</label>
                <Input value={newCommission.recipient_name} onChange={(e) => setNewCommission({...newCommission, recipient_name: e.target.value})} placeholder="Name" className="text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Type</label>
                <select value={newCommission.recipient_type} onChange={(e) => setNewCommission({...newCommission, recipient_type: e.target.value})} className="w-full border rounded px-2 py-2 text-sm">
                  <option value="ISO">ISO</option>
                  <option value="Broker">Broker</option>
                  <option value="Internal Rep">Internal Rep</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Amount ($)</label>
                <Input type="number" step="0.01" value={newCommission.amount} onChange={(e) => setNewCommission({...newCommission, amount: e.target.value})} placeholder="0.00" className="text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Notes</label>
                <Input value={newCommission.notes} onChange={(e) => setNewCommission({...newCommission, notes: e.target.value})} placeholder="Optional" className="text-sm" />
              </div>
              <Button size="sm" onClick={addCommission} className="bg-green-600 hover:bg-green-700">Save</Button>
            </div>
          )}

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">#</th>
                <th className="text-left py-2 px-3">Recipient</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-right py-2 px-3">Amount</th>
                <th className="text-right py-2 px-3">% of Funded</th>
                <th className="text-left py-2 px-3">Notes</th>
                {isAdmin && <th className="text-left py-2 px-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {commissions.length === 0 ? (
                <tr><td colSpan={isAdmin ? 7 : 6} className="py-3 text-center text-gray-400">No commissions</td></tr>
              ) : commissions.map((c, idx) => (
                <tr key={c.id} className="border-b">
                  <td className="py-2 px-3">{idx + 1}</td>
                  <td className="py-2 px-3 font-medium">{c.recipient_name}</td>
                  <td className="py-2 px-3"><span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{c.recipient_type}</span></td>
                  <td className="py-2 px-3 text-right font-medium">${(c.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td className="py-2 px-3 text-right">{(c.percentage || 0).toFixed(2)}%</td>
                  <td className="py-2 px-3 text-gray-500">{c.notes || '-'}</td>
                  {isAdmin && (
                    <td className="py-2 px-3">
                      <button onClick={() => deleteCommission(c.id)} className="text-red-600 hover:underline text-xs">Remove</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {commissions.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan="3" className="py-2 px-3 text-right">Total Commissions:</td>
                  <td className="py-2 px-3 text-right text-orange-600">${commissions.reduce((s, c) => s + (c.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td colSpan={isAdmin ? 3 : 2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Deal P&L */}
      {isFunded && (
        <div className="bg-white rounded-lg shadow p-6 mb-8" data-testid="deal-pnl-section">
          <h3 className="text-xl font-bold mb-4">Deal P&L</h3>
          {(() => {
            const collected = deal.collected || 0;
            const funded = deal.advanced || deal.funding_amount || 0;
            const netAmt = deal.net_amount != null ? deal.net_amount : (deal.net_funded != null ? deal.net_funded : funded);
            const expenses = deal.total_expenses != null
              ? deal.total_expenses
              : ((deal.uw_fee || 0) + (deal.legal_fee || 0) + (deal.filing_fee || 0) + (deal.ucc_fee || 0) + (deal.bank_fee || 0));
            const commTotal = deal.commission_total || 0;
            const synProfit = syndicators.reduce((sum, s) => sum + ((s.rtr || 0) - (s.participated_amount || 0)), 0);
            // Primary P&L per spec: collected − net wired
            const pnl = deal.deal_pnl != null ? deal.deal_pnl : (collected - netAmt);
            // Expanded view: also subtracts commissions, expenses, syndicator profit share
            const pnlNet = deal.deal_pnl_net_of_costs != null
              ? deal.deal_pnl_net_of_costs
              : (pnl - commTotal - expenses - synProfit);
            const margin = funded > 0 ? ((pnl / funded) * 100).toFixed(2) : '0.00';
            return (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Collected</p>
                  <p className="text-lg font-bold">${collected.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Net Wired</p>
                  <p className="text-lg font-bold text-orange-600">-${netAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Commissions</p>
                  <p className="text-lg font-bold text-orange-600">-${commTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-500">Expenses</p>
                  <p className="text-lg font-bold text-orange-600">-${expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className={`p-3 rounded ${pnl >= 0 ? 'bg-green-50' : 'bg-red-50'}`} data-testid="deal-pnl-summary-card">
                  <p className="text-xs text-gray-500">P&L (Collected − Net Wired)</p>
                  <p className={`text-lg font-bold ${pnl >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    ${pnl.toLocaleString('en-US', { minimumFractionDigits: 2 })} ({margin}%)
                  </p>
                  <p className={`text-[11px] mt-1 ${pnlNet >= 0 ? 'text-green-700' : 'text-red-500'}`} title="Also subtracts commissions, expenses & syndicator payouts">
                    Net of costs: ${pnlNet.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Syndicators */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Syndicators</h3>
          {isFunded && isAdmin && (
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => setShowAddSyndicator(true)} data-testid="add-syndicator-btn">
              <Plus size={16} className="mr-2" />
              Add Syndicator
            </Button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4">#</th>
              <th className="text-left py-2 px-4">Name</th>
              <th className="text-left py-2 px-4">Main Lender</th>
              <th className="text-left py-2 px-4">Amt. Participated</th>
              <th className="text-left py-2 px-4">Percentage</th>
              <th className="text-left py-2 px-4">Commission %</th>
              <th className="text-left py-2 px-4">RTR</th>
              <th className="text-left py-2 px-4">Commission Earned</th>
              <th className="text-left py-2 px-4">Net Take Home</th>
              <th className="text-left py-2 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {syndicators.length === 0 ? (
              <tr className="border-b bg-blue-50">
                <td className="py-2 px-4">1</td>
                <td className="py-2 px-4 font-medium">{deal.inhouse || ''}</td>
                <td className="py-2 px-4">✓</td>
                <td className="py-2 px-4">${(deal.advanced || 0).toLocaleString()}</td>
                <td className="py-2 px-4">100%</td>
                <td className="py-2 px-4">0%</td>
                <td className="py-2 px-4 text-green-600">${(deal.payback || 0).toLocaleString()}</td>
                <td className="py-2 px-4">$0</td>
                <td className="py-2 px-4 text-green-600 font-semibold">${(deal.payback || 0).toLocaleString()}</td>
                <td className="py-2 px-4 text-gray-400 text-xs">Owner</td>
              </tr>
            ) : (
              syndicators.map((syn, idx) => {
                const commPct = syn.commission_percent || 0;
                const rtr = syn.rtr || 0;
                const commEarned = rtr * (commPct / 100);
                const netTakeHome = rtr - commEarned;
                return (
                  <tr key={idx} className="border-b">
                    <td className="py-2 px-4">{idx + 1}</td>
                    <td className="py-2 px-4 font-medium">{syn.name}</td>
                    <td className="py-2 px-4">{syn.is_main_lender ? '✓' : ''}</td>
                    <td className="py-2 px-4">${syn.participated_amount?.toLocaleString()}</td>
                    <td className="py-2 px-4">{syn.percentage}%</td>
                    <td className="py-2 px-4">{commPct}%</td>
                    <td className="py-2 px-4 text-green-600">${rtr.toLocaleString()}</td>
                    <td className="py-2 px-4 text-red-600">${commEarned.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="py-2 px-4 text-green-600 font-semibold">${netTakeHome.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="py-2 px-4">
                      <div className="flex items-center gap-3">
                        <button
                          className="text-blue-600 hover:underline"
                          onClick={() => openEditSyndicator(syn)}
                          data-testid={`edit-syndicator-${syn.id}`}
                        >
                          Edit
                        </button>
                        <button 
                          className="text-red-600 hover:underline"
                          onClick={() => deleteSyndicator(syn.id)}
                          data-testid={`remove-syndicator-${syn.id}`}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {syndicators.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Total Syndicator RTR: ${syndicators.reduce((sum, s) => sum + (s.rtr || 0), 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  Total ISO CM: ${syndicators.reduce((sum, s) => sum + ((s.rtr || 0) * ((s.commission_percent || 0) / 100)), 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-green-700">
                  Your Profit: ${(() => {
                    const totalGrossProfit = (deal.payback || 0) - (deal.advanced || 0);
                    const synGrossProfit = syndicators.reduce((sum, s) => sum + ((s.rtr || 0) - (s.participated_amount || 0)), 0);
                    const commissionsEarned = syndicators.reduce((sum, s) => sum + ((s.rtr || 0) * ((s.commission_percent || 0) / 100)), 0);
                    return (totalGrossProfit - synGrossProfit + commissionsEarned).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
                  })()}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Deal gross profit minus syndicator profits plus your commissions earned</p>
          </div>
        )}
      </div>

      {/* Schedule Payments */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="text-xl font-bold mb-4">Schedule Payments</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4">Start Date</th>
              <th className="text-left py-2 px-4">End Date</th>
              <th className="text-left py-2 px-4">Next Payment Date</th>
              <th className="text-left py-2 px-4">Amount</th>
              <th className="text-left py-2 px-4">Term</th>
              <th className="text-left py-2 px-4">Frequency</th>
              <th className="text-left py-2 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b">
              <td className="py-2 px-4">{deal.date}</td>
              <td className="py-2 px-4">-</td>
              <td className="py-2 px-4">-</td>
              <td className="py-2 px-4">${deal.weekly || deal.daily}</td>
              <td className="py-2 px-4">{deal.term?.split('/')[0]}</td>
              <td className="py-2 px-4">{deal.daily ? 'Daily' : 'Weekly'}</td>
              <td className="py-2 px-4">Active</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Vendors & Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">Vendors</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-4">#</th>
                <th className="text-left py-2 px-4">Name</th>
              </tr>
            </thead>
            <tbody>
              <tr className="text-gray-500 text-center">
                <td colSpan="2" className="py-4">No vendors</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-bold mb-4">Related Documents</h3>
          {documents.length > 0 ? (
            <div className="space-y-3">
              {documents.map((doc, idx) => (
                <div 
                  key={idx} 
                  className="border rounded p-3 hover:bg-gray-50 cursor-pointer"
                  onClick={() => downloadDocument(doc.id, doc.name)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-sm text-blue-600 hover:underline">📄 {doc.name}</p>
                      <p className="text-xs text-gray-500">
                        Type: {doc.document_type} | Size: {(doc.size / 1024).toFixed(2)} KB
                      </p>
                      <p className="text-xs text-gray-500">
                        Uploaded: {new Date(doc.upload_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">No documents uploaded yet</p>
          )}
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Payments</h3>
          <Button variant="outline" size="sm">
            Export to Excel
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left py-2 px-4">#</th>
              <th className="text-left py-2 px-4">Bank Account</th>
              <th className="text-left py-2 px-4">Debit/Credit</th>
              <th className="text-left py-2 px-4">Scheduled date</th>
              <th className="text-left py-2 px-4">Payment date</th>
              <th className="text-left py-2 px-4">Provider</th>
              <th className="text-left py-2 px-4">Status</th>
              <th className="text-left py-2 px-4">Trs Type</th>
              <th className="text-left py-2 px-4">Payment</th>
              <th className="text-left py-2 px-4">Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr className="text-gray-500 text-center">
              <td colSpan="10" className="py-4">No payments recorded</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showFundingModal && (
        <FundingTermsModal 
          deal={deal} 
          onClose={() => setShowFundingModal(false)} 
          onSuccess={() => {
            setShowFundingModal(false);
            loadDeal();
          }} 
        />
      )}
      {showUploadDocs && (
        <UploadDocumentsDealModal 
          dealId={id} 
          onClose={() => setShowUploadDocs(false)} 
          onSuccess={() => {
            setShowUploadDocs(false);
            loadDocuments(); // Refresh documents list immediately
          }} 
        />
      )}
      {showNewPayment && (
        <NewPaymentModal 
          dealId={id} 
          onClose={() => setShowNewPayment(false)} 
          onSuccess={() => {
            setShowNewPayment(false);
            loadDeal();
          }} 
        />
      )}
      {showEditDeal && (
        <EditDealModal 
          deal={deal} 
          onClose={() => setShowEditDeal(false)} 
          onSuccess={() => {
            setShowEditDeal(false);
            loadDeal();
          }} 
        />
      )}
      {showRefund && (
        <RefundModal 
          dealId={id} 
          onClose={() => setShowRefund(false)} 
          onSuccess={() => {
            setShowRefund(false);
            loadDeal();
          }} 
        />
      )}
      {showAddSyndicator && (
        <AddSyndicatorModal 
          dealId={id}
          dealFunding={deal?.advanced || deal?.funding_amount || 0}
          dealPayback={deal?.payback || deal?.payback_amount || 0}
          onClose={() => setShowAddSyndicator(false)} 
          onSuccess={() => {
            setShowAddSyndicator(false);
            loadSyndicators();
          }} 
        />
      )}
      {showRefinance && (
        <RefinanceDealModal
          isOpen={showRefinance}
          onClose={() => setShowRefinance(false)}
          deal={deal}
          onSuccess={(newDealNum) => {
            setShowRefinance(false);
            // Navigate to the freshly-created deal
            if (newDealNum) {
              navigate(`/deals/${newDealNum}`);
            } else {
              loadDeal();
            }
          }}
        />
      )}
      {editingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="edit-payment-modal" onClick={() => setEditingPayment(null)}>
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Payment #{editingPayment.payment_number}</h3>
              <button onClick={() => setEditingPayment(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  value={editPaymentForm.amount}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  data-testid="edit-payment-amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Due Date</label>
                <input
                  type="date"
                  value={editPaymentForm.due_date}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, due_date: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  data-testid="edit-payment-due-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={editPaymentForm.status}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, status: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  data-testid="edit-payment-status"
                >
                  <option value="pending">Pending</option>
                  <option value="cleared">Cleared</option>
                  <option value="missed">Missed</option>
                  <option value="bounced">Bounced</option>
                  <option value="returned">Returned</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note</label>
                <input
                  type="text"
                  value={editPaymentForm.note}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, note: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  data-testid="edit-payment-note"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditingPayment(null)} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={saveEditPayment} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" data-testid="edit-payment-save">Save</button>
            </div>
          </div>
        </div>
      )}
      {deletingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="delete-payment-modal" onClick={() => setDeletingPayment(null)}>
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Payment #{deletingPayment.payment_number}?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Removes payment #{deletingPayment.payment_number} (${Number(deletingPayment.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) from the schedule.
              Collected, Remaining, and P&L will recalculate immediately.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeletingPayment(null)} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDeletePayment} className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700" data-testid="delete-payment-confirm">Delete</button>
            </div>
          </div>
        </div>
      )}
      {editingSyndicator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="edit-syndicator-modal" onClick={() => setEditingSyndicator(null)}>
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Syndicator — {editingSyndicator.name}</h3>
              <button onClick={() => setEditingSyndicator(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Participation %</label>
                <input
                  type="number"
                  step="0.01"
                  value={edSynForm.percentage}
                  onChange={(e) => setEdSynForm({ ...edSynForm, percentage: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  data-testid="edit-syn-percentage"
                />
                <p className="text-xs text-gray-500 mt-1">Share of the funded amount. Recomputes RTR ({((parseFloat(edSynForm.percentage) || 0) / 100 * (deal?.payback || 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}).</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Commission %</label>
                <input
                  type="number"
                  step="0.01"
                  value={edSynForm.commission_percent}
                  onChange={(e) => setEdSynForm({ ...edSynForm, commission_percent: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  data-testid="edit-syn-commission"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Commission charged on this syndicator's RTR. 
                  Fee share: ${((parseFloat(edSynForm.commission_percent) || 0) / 100 * ((parseFloat(edSynForm.percentage) || 0) / 100 * (deal?.payback || 0))).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditingSyndicator(null)} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50" data-testid="edit-syn-cancel">Cancel</button>
              <button onClick={saveSyndicatorEdit} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700" data-testid="edit-syn-save">Save</button>
            </div>
          </div>
        </div>
      )}
      {showStatusHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="status-history-modal">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <div className="flex items-center gap-2">
                <History size={18} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Status History</h3>
              </div>
              <button
                onClick={() => setShowStatusHistory(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="status-history-close"
              >✕</button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {statusHistory.length === 0 ? (
                <p className="text-sm text-gray-500">No status changes recorded yet.</p>
              ) : (
                <ul className="space-y-3">
                  {statusHistory.map((h) => {
                    const fromMeta = getStatusMeta(h.from_status);
                    const toMeta = getStatusMeta(h.to_status);
                    return (
                      <li key={h.id} className="rounded border border-gray-100 bg-gray-50 p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${fromMeta.pill}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${fromMeta.dot}`}></span>{fromMeta.label}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${toMeta.pill}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${toMeta.dot}`}></span>{toMeta.label}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
                          <span>by <strong>{h.changed_by_name || h.changed_by || 'Unknown'}</strong></span>
                          <span>{h.changed_at ? new Date(h.changed_at).toLocaleString() : ''}</span>
                        </div>
                        {h.reason && (
                          <div className="mt-1 text-xs text-gray-700 italic">"{h.reason}"</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealDetail;
